/**
 * [ADAM AUDIO batch 2 item 1] Draft-order reveal copy.
 *
 * Rotating TEMPLATES, not LLM (founder Q3a): instant, free, deterministic,
 * and - critically - no model latency stalling an audio-gated sequence
 * where every card flip waits on the previous clip finishing. The rotation
 * is deep enough that a 12-team reveal never repeats a line, and it is
 * drawn as a shuffled deck rather than sampled independently, so "never
 * the same line twice" is structural instead of lucky.
 *
 * All of these are spoken through `/api/tts/speak`, which runs the
 * abbreviation scrubber at the synthesis chokepoint - so a league team
 * name carrying a number or an abbreviation ("49ers Fan Club", "2026 Title
 * Chasers", "KC Faithful") is expanded the same way any other Adam line
 * is. That is the whole reason the reveal speaks through the queue instead
 * of pre-rendered clips.
 */

const INTRO_LINES = {
  auction: [
    "Alright, let's kick off the nomination order! Twelve managers, one board, no mercy.",
    "Here we go - the nomination order is in, and somebody's about to get the first swing.",
    "Locking in the nomination order right now. Deep breath, everybody.",
  ],
  snake: [
    "Alright, let's kick off the picking order! Somebody's walking away with the first pick.",
    "Here we go - the picking order is in. Let's find out who's on the clock first.",
    "Locking in the picking order right now. Deep breath, everybody.",
  ],
};

const FIRST_LINES = [
  "The first team to pick is {team}!",
  "Kicking us off, it's {team}!",
  "Right at the top of the board - {team}!",
  "First up, and feeling great about it - {team}!",
];

const MIDDLE_LINES = [
  "Next up, {team}.",
  "In at number {n}, {team}.",
  "Slotting in at {n} - {team}.",
  "Then it's {team} at {n}.",
  "{team} lands at number {n}.",
  "Number {n} on the board - {team}.",
  "After that, {team}.",
  "{team}, you're at {n}.",
  "Coming in at {n}, {team}.",
  "And at number {n}, it's {team}.",
  "{team} takes the {n} slot.",
  "Following them, {team} at {n}.",
  "{team} settles in at number {n}.",
  "Next on the list, {team} at {n}.",
];

const LAST_LINES = [
  "And bringing up the rear - {team}. Somebody has to be last.",
  "Rounding it out at the very bottom, {team}.",
  "And last but definitely loudest about it - {team}.",
  "Anchoring the order, {team}.",
];

const ORDINALS = [
  "", "one", "two", "three", "four", "five", "six", "seven", "eight",
  "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen",
  "sixteen", "seventeen", "eighteen", "nineteen", "twenty",
];

function numberWord(n) {
  return ORDINALS[n] || String(n);
}

function shuffled(pool, rng) {
  const deck = [...pool];
  for (let i = deck.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
}

export function revealIntroLine(format, rng = Math.random) {
  const pool = INTRO_LINES[format === "snake" ? "snake" : "auction"];
  return pool[Math.floor(rng() * pool.length)];
}

/**
 * One line per team, in order. Middle lines are dealt from a shuffled
 * deck (reshuffled only if a league is deeper than the deck), so no
 * middle phrasing repeats inside a single reveal.
 */
export function revealLines(teamNames, rng = Math.random) {
  const total = teamNames.length;
  let deck = shuffled(MIDDLE_LINES, rng);
  let cursor = 0;
  return teamNames.map((team, idx) => {
    const slot = idx + 1;
    let template;
    if (idx === 0) {
      template = shuffled(FIRST_LINES, rng)[0];
    } else if (idx === total - 1 && total > 2) {
      template = shuffled(LAST_LINES, rng)[0];
    } else {
      if (cursor >= deck.length) {
        deck = shuffled(MIDDLE_LINES, rng);
        cursor = 0;
      }
      template = deck[cursor];
      cursor += 1;
    }
    return template
      .replace("{team}", team)
      .replace("{n}", numberWord(slot));
  });
}
